import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiGrid, FiUsers } from 'react-icons/fi';
import {
  CHART_PALETTE,
  CHART_FONT,
  CHART_GRID,
  CHART_CATEGORY_AXIS,
  CHART_TOOLTIP,
  CHART_SEMANTIC
} from 'helpers/echartsTheme';

const COLORS = CHART_PALETTE;
const SIGNED_COLOR = CHART_SEMANTIC.success;

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmt = n => Number(n || 0).toLocaleString('en-US');

// ── Column chart (ECharts) ────────────────────────────────────────────────

const CHART_H = 150;

const ColumnChart = ({ rows, labelKey = 'unit_name', labelSubKey = null }) => {
  const maxValue = Math.max(...rows.map(d => d.total_value), 1);

  const option = {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { ...CHART_GRID, top: 8, bottom: 0 },
    tooltip: {
      ...CHART_TOOLTIP,
      trigger: 'item',
      formatter: (params) => {
        const item  = rows[params.dataIndex];
        const color = COLORS[params.dataIndex % COLORS.length];
        if (!item) return '';
        const lines = [
          `<div style="font-weight:700;color:#111827">${item[labelKey] || ''}</div>`
        ];
        if (labelSubKey && item[labelSubKey]) {
          lines.push(`<div style="font-size:11px;color:#9ca3af">${item[labelSubKey]}</div>`);
        }
        lines.push(
          `<div style="font-size:11px;color:#6b7280;margin-top:2px">${fmt(item.project_count)} projects</div>`,
          `<div style="font-size:14px;font-weight:800;margin-top:2px;color:${color}">${fmtM(item.total_value)}</div>`,
          `<div style="font-size:11px;color:${SIGNED_COLOR}">Signed: ${fmtM(item.signed_value)}</div>`
        );
        if (item.total_weight > 0) {
          lines.push(`<div style="font-size:11px;font-weight:600;color:#0891b2">Weight: ${fmt(Math.round(item.total_weight))} MT</div>`);
        }
        lines.push(`<div style="font-size:11px;color:${CHART_SEMANTIC.danger}">Lost: ${fmt(item.lost_count)}</div>`);
        return lines.join('');
      }
    },
    xAxis: {
      type: 'category',
      ...CHART_CATEGORY_AXIS,
      data: rows.map(item => (item[labelKey] || '').slice(0, 9)),
      axisLabel: {
        interval: 0,
        formatter: (value, idx) => `{name|${value}}\n{sub|${fmt(rows[idx]?.project_count)} proj}`,
        rich: {
          name: { fontSize: 10, fontWeight: 600, color: '#374151', lineHeight: 14 },
          sub:  { fontSize: 9, color: '#9CA3AF', lineHeight: 12 }
        }
      }
    },
    yAxis: { type: 'value', show: false },
    series: [
      {
        type: 'bar',
        barWidth: 48,
        z: 1,
        data: rows.map((item, i) => ({
          value: item.total_value,
          itemStyle: {
            color: COLORS[i % COLORS.length],
            opacity: 0.8,
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            // Same rule as the SVG version: only show when the bar is tall enough
            show: (item.total_value / maxValue) * CHART_H > 22
          }
        })),
        label: {
          position: 'insideTop',
          fontSize: 9,
          fontWeight: 700,
          color: '#ffffff',
          formatter: (p) => fmtM(rows[p.dataIndex]?.total_value)
        }
      },
      {
        type: 'bar',
        barWidth: 48,
        barGap: '-100%',
        z: 2,
        silent: true,
        itemStyle: { color: SIGNED_COLOR, opacity: 0.6, borderRadius: [4, 4, 0, 0] },
        data: rows.map(item => item.signed_value)
      }
    ]
  };

  return (
    <div className="tw-overflow-x-auto">
      <ReactECharts
        option={option}
        style={{ height: CHART_H + 55, width: '100%', minWidth: Math.max(rows.length * 72, 300) }}
        notMerge
      />
    </div>
  );
};
ColumnChart.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    total_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    signed_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    project_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lost_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  labelKey: PropTypes.string,
  labelSubKey: PropTypes.string,
};

// ── Main component ────────────────────────────────────────────────────────

const SalesUnitChart = ({ data, loading, error }) => {
  const [view, setView] = useState('region');

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading sales unit data</p>
    </Card>
  );

  // Support both old array format and new { by_region, by_unit } format
  const byRegion = data?.by_region || (Array.isArray(data) ? data : []);
  const byUnit   = data?.by_unit   || [];

  const rows = view === 'region' ? byRegion : byUnit;

  if (!rows || rows.length === 0) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
    </Card>
  );

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-blue-50 tw-shadow-sm tw-h-full">
      <div className="tw-p-5 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
        <div>
          <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
            {view === 'region'
              ? <FiGrid className="tw-text-slate-600" size={18} />
              : <FiUsers className="tw-text-blue-600" size={18} />
            }
            {view === 'region' ? 'By Region' : 'By Team (Sales Unit)'}
          </h3>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
            {view === 'region'
              ? 'Pipeline value per region (deduplicated)'
              : 'Pipeline value per sales team'
            }
          </p>
        </div>

        {/* Toggle tabs */}
        <div className="tw-flex tw-bg-gray-100 tw-rounded tw-p-0.5">
          {[
            { key: 'region', label: 'Region', icon: FiGrid },
            { key: 'unit',   label: 'Team',   icon: FiUsers }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all tw-cursor-pointer ${
                view === key
                  ? 'tw-bg-white tw-text-blue-700 tw-shadow-sm'
                  : 'tw-text-gray-500 hover:tw-text-gray-700'
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="tw-p-5">
        {view === 'region' ? (
          <ColumnChart rows={byRegion} labelKey="unit_name" />
        ) : (
          <ColumnChart rows={byUnit} labelKey="unit_name" labelSubKey="region" />
        )}
      </div>

      {/* Legend */}
      <div className="tw-px-5 tw-pb-4 tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-gray-500">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-w-3 tw-h-3 tw-rounded-sm tw-inline-block" style={{ background: COLORS[0], opacity: 0.8 }} />
          Pipeline Value
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-w-3 tw-h-3 tw-rounded-sm tw-inline-block" style={{ background: SIGNED_COLOR, opacity: 0.6 }} />
          Signed (overlay)
        </div>
      </div>
    </Card>
  );
};

SalesUnitChart.propTypes = {
  data:    PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  loading: PropTypes.bool,
  error:   PropTypes.any
};
SalesUnitChart.defaultProps = { data: null, loading: false, error: null };

export default SalesUnitChart;
